import { UserAgent, FullscreenHelper } from "@blacknut/javascript-sdk/dist";
import { isErrorResponse, MouseInputMode } from ".";
import { eventEmitter, PlayerEvents } from "./EventEmitter";
import { WebRTCClient, WindowMode } from "./WebRTCClient";
import {
  AddIceCandidateResponse,
  ErrorResponse,
  NegotiationMessage,
  WebSocketNegotiation,
} from "./WebSocketNegotiation";
import { formatIceServers, LOGGING_TAG } from "../utils/logging";
import { logD, logE, logI } from "@blacknut/logging/dist";
const MAX_RECONNECT = 5;

//2:30 mins max time to try to reconnect on focus
const MAX_IDLE_TIME = 2.5 * 60;

export class PlayerWebRTC {
  argurl;
  token;
  WebRTCClient: WebRTCClient = null;
  webSocketServerMain;
  videoTag;
  listeners;
  contentNode;
  sdpOptions;

  webSocketNegotiation: WebSocketNegotiation;

  private streamerVersionOnDisconnect?: number[];

  private _mouseEnabled = true;
  public get mouseEnabled(): boolean {
    return this._mouseEnabled;
  }
  public set mouseEnabled(v: boolean) {
    this._mouseEnabled = v;
  }

  private _filteredMimeTypes: string[] | undefined;
  public get filteredMimeTypes(): string[] | undefined {
    return this._filteredMimeTypes;
  }
  public set filteredMimeTypes(v: string[] | undefined) {
    this._filteredMimeTypes = v;
  }

  private _iceServers: RTCIceServer[] | undefined;
  public get iceServers(): RTCIceServer[] | undefined {
    return this._iceServers;
  }

  public set iceServers(v: RTCIceServer[] | undefined) {
    this._iceServers = v;
  }

  private _iceTransportPolicy: RTCIceTransportPolicy = "all";
  public get iceTransportPolicy(): RTCIceTransportPolicy {
    return this._iceTransportPolicy;
  }
  public set iceTransportPolicy(v: RTCIceTransportPolicy) {
    this._iceTransportPolicy = v;
  }

  private _privateRelay: boolean | undefined;
  public get privateRelay(): boolean | undefined {
    return this._privateRelay;
  }
  public set privateRelay(v: boolean | undefined) {
    this._privateRelay = v;
  }

  private _acquireMicro: boolean | undefined;
  public get acquireMicro(): boolean | undefined {
    return this._acquireMicro;
  }
  public set acquireMicro(v: boolean | undefined) {
    this._acquireMicro = v;
  }

  private _useDesktopCrop: boolean | undefined;
  public get useDesktopCrop(): boolean | undefined {
    return this._useDesktopCrop;
  }
  public set useDesktopCrop(v: boolean | undefined) {
    this._useDesktopCrop = v;
  }

  private _reconnectCount: number = MAX_RECONNECT;
  public get reconnectCount(): number {
    return this._reconnectCount;
  }
  public set reconnectCount(v: number) {
    this._reconnectCount = v;
  }

  private _mouseInputMode: MouseInputMode = "absolute";
  public get mouseInputMode(): MouseInputMode {
    return this._mouseInputMode;
  }
  public set mouseInputMode(v: MouseInputMode) {
    this._mouseInputMode = v;
  }

  private _preferedWindowMode: WindowMode = "fullscreen";
  public get preferedWindowMode(): WindowMode {
    return this._preferedWindowMode;
  }
  public set preferedWindowMode(v: WindowMode) {
    this._preferedWindowMode = v;
  }

  private _userAgent: UserAgent;
  public get userAgent(): UserAgent {
    return this._userAgent;
  }
  public set userAgent(v: UserAgent) {
    this._userAgent = v;
  }

  private _onBeforeUnload?: OnBeforeUnloadEventHandler;
  public get onBeforeUnload(): OnBeforeUnloadEventHandler | undefined {
    return this._onBeforeUnload;
  }
  public set onBeforeUnload(v: OnBeforeUnloadEventHandler | undefined) {
    this._onBeforeUnload = v;
  }

  private _embedded = false;
  public get embedded(): boolean {
    return this._embedded;
  }
  public set embedded(v: boolean) {
    this._embedded = v;
  }

  private _statsInterval = 5000;
  public get statsInterval(): number {
    return this._statsInterval;
  }
  public set statsInterval(v: number) {
    this._statsInterval = v;
  }

  private _fullscreenHelper: FullscreenHelper | undefined;
  public get fullscreenHelper(): FullscreenHelper | undefined {
    return this._fullscreenHelper;
  }
  public set fullscreenHelper(v: FullscreenHelper | undefined) {
    this._fullscreenHelper = v;
  }
  private _autoplay = false;
  public get autoplay(): boolean {
    return this._autoplay;
  }
  public set autoplay(v: boolean) {
    this._autoplay = v;
  }

  private _mute = false;
  public get mute(): boolean {
    return this._mute;
  }
  public set mute(v: boolean) {
    this._mute = v;
  }

  private _streamId: string | undefined;
  public get streamId(): string | undefined {
    return this._streamId;
  }
  public set streamId(v: string | undefined) {
    this._streamId = v;
  }

  constructor(argurl: string, token: string, tag?) {
    this.listeners = new Map();
    this.videoTag = tag || "video_player";
    this.argurl = argurl;
    this.token = token;
    this.WebRTCClient = null;
  }

  /**
   * Create WebSocket Negotiation
   */
  init(contentNode, sdpOptions?) {
    this.contentNode = contentNode;
    this.sdpOptions = sdpOptions;
    this.webSocketNegotiation = new WebSocketNegotiation({
      url: `${this.argurl}${this.token ? `?token=${this.token}` : ""}`,
      namespace: "webrtc-negociation",
      onClose: () => this.onWebSocketClose(),
      onError: (error) => this.onWebSocketError(error),
      onReady: (data) => this.onSocketReady(data),
      onMessage: (m) => this.onWebSocketMessage(m),
      userAgent: this.userAgent,
    });
    logI(
      LOGGING_TAG,
      "In init codecs=%o ice=%o servers %o, micro:%o, privateRelay:%o, useDesktopCrop:%o, reconnectCount:%o",
      this.filteredMimeTypes,
      this.iceTransportPolicy,
      formatIceServers(this.iceServers),
      this.acquireMicro,
      this.privateRelay,
      this.useDesktopCrop,
      this.reconnectCount,
    );

    if (!this.onBeforeUnload) {
      window.addEventListener("beforeunload", this.delete);
    }
  }

  onWebSocketClose = () => {
    this._delete();
  };
  onWebSocketError = (error: Event) => {
    logE(LOGGING_TAG, "Websocket negotiation error %o", error);

    // on iOS, when user ends game using in game menu, error is triggered on negociation socket
    // fix is to check if user has played game.
    // If so we can safely ignore it
    if (!this.WebRTCClient || !this.WebRTCClient.hasPlayed) {
      if (!this.canReconnect()) {
        eventEmitter.emit(PlayerEvents.ERROR, "Websocket negociation error");
      } else {
        this._delete();
      }
    }
  };

  private onWebSocketErrorResponse = (response: ErrorResponse) => {
    logE(
      LOGGING_TAG,
      "Error received from server %o (%o)",
      response.error.message,
      response.error.code,
    );
    this._delete();
  };
  onWebSocketMessage = (data: NegotiationMessage) => {
    //logE(LOGGING_TAG,"Websocket negotiation message %o", data);

    if (isErrorResponse(data.json_data)) {
      this.onWebSocketErrorResponse(data.json_data);
      return;
    }
    if (data.method === "hangup") {
      logD(LOGGING_TAG, "hangup received, delete conx");
      this.deleteCalled = true;
      this.reconnectCount = 0; // do not try to reconnect
      this._delete(true);
    }
    if (data.method === "addIceCandidate") {
      const candidate: RTCIceCandidate = (data.json_data as AddIceCandidateResponse)
        .body;
      logI(LOGGING_TAG, "Adding ICE candidate from remote: %o", candidate);
      if (candidate) {
        this.WebRTCClient.addRemoteIceCandidate(candidate)
          .then(() => {
            //Already logged
          })
          .catch(() => {
            //Already logged
          });
      }
    }
  };

  /**
   * Create a WebRTCClient connection
   */
  private onSocketReady(data: unknown) {
    // getIceServers
    if (isErrorResponse(data)) {
      this.onWebSocketErrorResponse(data);
      return;
    }

    // when muskular and desktop crop not explicitly set
    if (
      this.useDesktopCrop === undefined &&
      this.webSocketNegotiation.streamerVersion[0] >= 2
    ) {
      // use the getConfig property that says whether video shall be
      // upscaled if not as big as videowidth/height, e.g. the opposite
      // of desktop crop
      if (this.webSocketNegotiation.streamingSize.upscaleSmallerVideo === true) {
        logI(LOGGING_TAG, "Force upscale smaller video");
        this.useDesktopCrop = false;
      } else {
        logI(LOGGING_TAG, "Force desktop crop");
        this.useDesktopCrop = true;
      }
    }

    const conf = data as RTCConfiguration;
    logI(LOGGING_TAG, "On socket ready. Remote conf: %o", {
      ...conf,
      iceServers: formatIceServers(conf.iceServers),
    });
    conf || { iceServers: [] };

    if (this._iceServers) {
      conf.iceServers = this._iceServers || [];
    }
    conf.iceTransportPolicy = this.iceTransportPolicy;
    conf.iceServers = conf.iceServers.map((e) => {
      if (!e.hasOwnProperty("urls") && e.hasOwnProperty("url")) {
        return { ...e, urls: [e["url"]] };
      }
      return e;
    });
    const pcConfig = conf || { iceServers: [] };

    logI(LOGGING_TAG, "Config %o", {
      ...pcConfig,
      iceServers: formatIceServers(pcConfig.iceServers),
    });

    // connect video element to webrtc stream
    this.WebRTCClient = new WebRTCClient(
      this.contentNode,
      this.argurl,
      this.token,
      pcConfig,
      this.videoTag,
      this.webSocketNegotiation,
      this.sdpOptions,
    );
    this.WebRTCClient.mouseEnabled = this.mouseEnabled;
    this.WebRTCClient.filteredMimeTypes = this.filteredMimeTypes;
    this.WebRTCClient.privateRelay = this.privateRelay;
    this.WebRTCClient.acquireMicro = this.acquireMicro;
    this.WebRTCClient.useDesktopCrop = this.useDesktopCrop;
    this.WebRTCClient.streamingSize = this.webSocketNegotiation.streamingSize;
    this.WebRTCClient.mouseInputMode = this.mouseInputMode;
    this.WebRTCClient.embedded = this.embedded;
    this.WebRTCClient.statsInterval = this._statsInterval;
    this.WebRTCClient.fullscreenHelper = this.fullscreenHelper;
    this.WebRTCClient.autoplay = this.autoplay;
    this.WebRTCClient.onFatalErrorResponse = this.onWebSocketErrorResponse.bind(this);
    this.WebRTCClient.mute = this.mute;
    this.WebRTCClient.onDisconnect = () => {
      this._delete();
    };
    this.WebRTCClient.streamId = this.streamId;
    this.WebRTCClient.connect();

    //2:30 mins max time to try to reconnect on focus
    // const MAX_IDLE_TIME = (2 * 60 + 30) * 1000;

    this.streamerVersionOnDisconnect = this.webSocketNegotiation.streamerVersion;
    this.reconnectCount = MAX_RECONNECT;
    this.WebRTCClient.preferedWindowMode = this.preferedWindowMode;
    this.WebRTCClient.userAgent = this.userAgent;
  }

  private deleteCalled = false;
  public delete() {
    if (this.deleteCalled) {
      return; // prevent calling twice
    }
    this.deleteCalled = true;
    this.reconnectCount = 0; //Do not try to reconnect
    this._delete(true);
  }

  private canReconnect() {
    const currentTime = new Date().getTime() / 1000;
    logD(
      LOGGING_TAG,
      "In canReconnect, lastFramesDecodedTimestampSecond: %o, currentTime: %o",
      this.WebRTCClient?.lastFramesDecodedTimestampSecond,
      currentTime,
    );
    const res =
      this.reconnectCount > 0 &&
      this.streamerVersionOnDisconnect &&
      this.streamerVersionOnDisconnect[0] > 1 &&
      (!this.WebRTCClient?.lastFramesDecodedTimestampSecond ||
        currentTime - this.WebRTCClient?.lastFramesDecodedTimestampSecond <
          MAX_IDLE_TIME);
    logD(LOGGING_TAG, "Out canReconnect ==> %o", res);
    return res;
  }

  /**
   * Delete a webrtc client connection
   */
  private _delete(hangup?: boolean) {
    const canReconnect = this.canReconnect();
    logD(
      LOGGING_TAG,
      "Destroying (reconnectCount=%o, canReconnect%o)",
      this.reconnectCount,
      canReconnect,
    );

    // disconnect webrtc connection
    if (this.WebRTCClient) {
      if (canReconnect) {
        this.WebRTCClient.reconnect();
      } else {
        this.WebRTCClient.disconnect(hangup);
      }
    }

    if (canReconnect) {
      this.reconnectCount--;
      eventEmitter.emit(PlayerEvents.RECONNNECTING);
      setTimeout(() => {
        this.init(this.contentNode, this.sdpOptions);
      }, 5000);
    } else {
      if (this._onBeforeUnload) {
        window.removeEventListener("beforeunload", this.delete);
      }
    }
  }

  /**
   * WebRTCClient function bind in player to only use playerWebRTC interface.
   */
  bind() {
    this.WebRTCClient && this.WebRTCClient.bindInputs();
  }
  unBind() {
    this.WebRTCClient && this.WebRTCClient.unbindInputs();
  }

  /**
   * EvenEmitter function bind in player to only use playerWebRTC interface.
   */
  on(event: string, listener) {
    return eventEmitter.on(event, listener);
  }
  off(event, listener) {
    return eventEmitter.off(event, listener);
  }

  /**
   * WebRTCClient function to handle user interactions on play and resume
   */
  onEndUserPlay() {
    this.WebRTCClient && this.WebRTCClient.onEndUserPlay();
  }
  onEndUserResume() {
    this.WebRTCClient && this.WebRTCClient.onEndUserResume();
  }
  onEndUserRequestFullscreen() {
    this.WebRTCClient && this.WebRTCClient.onEndUserRequestFullscreen();
  }
}
